import './WorkflowComments.scss';
import React, { useState, useCallback, useEffect } from "react";
import { Applications } from "../../api/Applications";
import { BdaApplicationComment } from "../../models/BdaApplication";
import { Alert, Button, Container } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import AppLoader from "../common/AppLoader";
import { useToasts } from "../../contexts/toasts";
import classNames from "classnames";
import { Flex } from 'antd';
import Avatar from '../common/Avatar';
import { useAuth } from '../../contexts/auth';
import { useFullScreenLoader } from '../../contexts/fullScreenLoader';
import { formatDate } from '../../utils';
import IconButton from '../common/IconButton';

type Props = {
  applicationId: number;
}

export default function WorkflowComments({ applicationId }: Props) {
  const { showToast } = useToasts();
  const { loggedUser } = useAuth();
  const { showLoader, hideLoader } = useFullScreenLoader();
  const [comments, setComments] = useState<BdaApplicationComment[]>();
  const [error, setError] = useState(false);
  const [inputText, setInputText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editingComment, setEditingComment] = useState<BdaApplicationComment>();

  const retrieveWorkflowComments = useCallback(() => {
    const params = new URLSearchParams([['sort', 'id']]);
    // params.append("desc", "true");
    Applications.getWorkflowComments(applicationId, params)
      .then(
        ({ data }) => setComments(data),
        (err: any) => {
          setError(true);
          setComments([]);
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('comments-error', message, `Comments Error ${status ? `(${status})` : ''}`, 'danger');
        }
      );
  }, [applicationId, showToast]);

  const submitComment = useCallback(() => {
    const newComment = new BdaApplicationComment(applicationId, inputText);
    setSubmitting(true);

    Applications.createComment(newComment)
      .then(
        () => {
          setSubmitting(false);
          setInputText('');
          retrieveWorkflowComments();
        },
        (err: any) => {
          setSubmitting(false);
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('comment-create-error', message, `Comments Error ${status ? `(${status})` : ''}`, 'danger');
        }
      );
  }, [applicationId, inputText, retrieveWorkflowComments, showToast]);

  const updateComment = useCallback(() => {
    setEditSubmitting(true);

    Applications.updateComment(editingComment!.id!, applicationId, editingComment!)
      .then(
        () => {
          setEditSubmitting(false);
          setEditSubmitting(false);
          setEditingComment(undefined);
          showToast('update-comment-success', "Comment successfully updated", "Update Comment success", 'success');
          retrieveWorkflowComments();
        },
        (err: any) => {
          setEditSubmitting(false);
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('comment-update-error', message, `Comments Error ${status ? `(${status})` : ''}`, 'danger');
        }
      );
  }, [applicationId, retrieveWorkflowComments, showToast, editingComment]);

  useEffect(() => retrieveWorkflowComments(), [retrieveWorkflowComments]);

  const handleEditComment = useCallback((comment: BdaApplicationComment) => () => setEditingComment(comment), []);
  const handleEditingCommentTextChange = useCallback((text: string) => setEditingComment((prev) => ({ ...prev!, text })), []);
  const handleEditModeClose = useCallback(() => setEditingComment(undefined), []);

  const deleteComment = useCallback((comment: BdaApplicationComment) => () => {
    showLoader();

    Applications.deleteComment(comment.id!, applicationId)
      .then(
        () => {
          showToast('comment-delete-success', "Comment deleted successfully.", 'Success', 'success');
          hideLoader();
          retrieveWorkflowComments();
        },
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('comment-delete-error', message, `Comment Error ${status ? `(${status})` : ''}`, 'danger');
          hideLoader();
        }
      )
  }, [showLoader, hideLoader, showToast, applicationId, retrieveWorkflowComments]);

  const renderComments = useCallback(() => {
    if (error) return (
      <Alert variant="danger" className="py-2">
        <FontAwesomeIcon icon={solid('exclamation-triangle')} />
        <span className="ms-2">An error occurred, please try again later.</span>
      </Alert>
    );

    return comments!.length === 0
      ? (
        <div className="comments-empty">
          <FontAwesomeIcon icon={solid('comments')} className="comments-empty-icon mb-2" />
          <h6 className="mb-0">No comments yet</h6>
          <p className="comments-empty-subtitle">Be the first to leave a comment!</p>
        </div>
      )
      : (
        <div className="comments-list">
          {comments!.map((comment, index) => {
            const authorName = comment.author ? comment.author.name! : 'Unknown';
            const myComment = !comment.deleted && comment.author?.id === loggedUser?.sub;
            return (
              <Flex gap={12} key={index} className={classNames("mb-2 comment-item p-2 w-100", { "my-comment": myComment })}>
                <div className="flex-shrink-0">
                  <Avatar size="sm" letters={authorName.substring(0, 1)} />
                </div>
                {editingComment && editingComment.id! === comment.id!
                  ? (
                    <CommentInput
                      disabled={editSubmitting}
                      value={editingComment.text}
                      onChange={handleEditingCommentTextChange}
                      actions={
                        <>
                          <Button
                            size="sm"
                            disabled={editSubmitting || !editingComment.text.trim()}
                            onClick={updateComment}
                            variant="success"
                          >
                            <FontAwesomeIcon icon={solid('check')} size="lg" />
                            <span className="ms-2">Confirm</span>
                          </Button>
                          <Button
                            size="sm"
                            onClick={handleEditModeClose}
                            disabled={editSubmitting}
                            variant="light"
                            className="border ms-2"
                          >
                            Cancel
                          </Button>
                        </>
                      }
                    />
                  )
                  : (
                    <div className="flex-grow-1">
                      <Flex justify="space-between" align="center" className="w-100">
                        <div>
                          <b className="me-1">{authorName}</b>
                          <span className="font-sm ms-2 text-muted">{formatDate(comment.updatedAt!)}</span>
                        </div>
                        {myComment && (
                          <Flex className="comment-actions">
                            <IconButton tooltip="Edit" onClick={handleEditComment(comment)} className="p-1">
                              <FontAwesomeIcon icon={solid("pen")} />
                            </IconButton>
                            <IconButton tooltip="Delete" onClick={deleteComment(comment)} className="text-danger p-1 ms-2">
                              <FontAwesomeIcon icon={solid("trash")} />
                            </IconButton>
                          </Flex>
                        )}
                      </Flex>
                      <span className={classNames("comment-text w-100", { "text-secondary fst-italic": comment.deleted })}>{comment.deleted ? "This comment has been deleted" : comment.text}</span>
                    </div>
                  )}
              </Flex>
            );
          })}
        </div>
      );
  }, [comments, error, loggedUser, editingComment, handleEditingCommentTextChange, deleteComment, handleEditComment, editSubmitting, handleEditModeClose, updateComment]);

  const renderAsyncContent = useCallback(() => {
    return comments
      ? renderComments()
      : <AppLoader className="py-5 my-5" justify="center" size={100} />;
  }, [renderComments, comments]);

  return (
    <Container fluid className="m-0 p-0">
      {renderAsyncContent()}

      <CommentInput
        disabled={submitting}
        value={inputText}
        onChange={setInputText}
        actions={
          <Button
            disabled={submitting || !inputText.trim()}
            onClick={submitComment}
            variant="primary"
            className="me-2"
          >
            <FontAwesomeIcon icon={solid("comment")} />
            <span className="ms-2">Submit</span>
          </Button>
        }
      />
    </Container>
  )
}

type CommentInputProps = {
  placeholder?: string;
  value?: string;
  disabled?: boolean;
  onChange?: (value: string) => void;
  actions?: React.ReactNode;
  rows?: number;
}

function CommentInput({ placeholder = "Write comment here...", value, onChange, actions, disabled, rows = 3 }: CommentInputProps) {
  const handleTextChange = useCallback((evt: React.ChangeEvent<HTMLTextAreaElement>) => onChange?.(evt.target.value), [onChange]);

  return (
    <Flex align="center" className={classNames("comment-input-wrapper", { disabled })}>
      <textarea
        className="comment-input"
        rows={rows}
        placeholder={placeholder}
        value={value}
        onChange={handleTextChange}
        disabled={disabled}
      />
      <div className="comment-toolbar">{actions}</div>
    </Flex>
  )
}

